import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { db } from "@/lib/firebase";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { MessageSquare, Send, Search, Plus } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface Conversation {
  id: string;
  participantId: string;
  participantName: string;
  participantType: 'buyer' | 'seller' | 'broker' | 'consultant';
  lastMessage: string;
  lastMessageAt: Date;
  unreadCount: number;
  mandateId?: string;
  mandateName?: string;
}

interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  senderName: string;
  content: string;
  sentAt: Date;
  read: boolean;
}

const mockConversations: Conversation[] = [
  {
    id: "conv-1",
    participantId: "client-1",
    participantName: "Jean-Pierre Martin",
    participantType: "buyer",
    lastMessage: "Thank you for the update. I'll review the documents.",
    lastMessageAt: new Date(Date.now() - 2 * 60 * 60 * 1000),
    unreadCount: 2,
    mandateId: "mandate-1",
    mandateName: "Due Diligence Review",
  },
  {
    id: "conv-2",
    participantId: "client-2",
    participantName: "Sophie Tremblay",
    participantType: "seller",
    lastMessage: "I'll send the required documents today.",
    lastMessageAt: new Date(Date.now() - 24 * 60 * 60 * 1000),
    unreadCount: 0,
  },
];

const mockMessages: Message[] = [
  {
    id: "msg-1",
    conversationId: "conv-1",
    senderId: "client-1",
    senderName: "Jean-Pierre Martin",
    content: "Hello, I have a question about the contract.",
    sentAt: new Date(Date.now() - 4 * 60 * 60 * 1000),
    read: true,
  },
  {
    id: "msg-2",
    conversationId: "conv-1",
    senderId: "partner",
    senderName: "You",
    content: "Of course! How can I help you?",
    sentAt: new Date(Date.now() - 3.5 * 60 * 60 * 1000),
    read: true,
  },
  {
    id: "msg-3",
    conversationId: "conv-1",
    senderId: "client-1",
    senderName: "Jean-Pierre Martin",
    content: "Thank you for the update. I'll review the documents.",
    sentAt: new Date(Date.now() - 2 * 60 * 60 * 1000),
    read: false,
  },
];

const translations = {
  en: {
    title: "Messages",
    subtitle: "Communicate with your clients",
    newMessage: "New Message",
    searchConversations: "Search conversations...",
    conversations: "Conversations",
    selectConversation: "Select a conversation",
    noConversations: "No conversations yet",
    typeMessage: "Type your message...",
    send: "Send",
    you: "You",
    buyer: "Buyer",
    seller: "Seller",
    broker: "Broker",
    consultant: "Consultant",
    unread: "unread",
    messageSent: "Message Sent",
    messageSentDesc: "Your message was sent successfully",
  },
  fr: {
    title: "Messages",
    subtitle: "Communiquez avec vos clients",
    newMessage: "Nouveau Message",
    searchConversations: "Rechercher conversations...",
    conversations: "Conversations",
    selectConversation: "Sélectionnez une conversation",
    noConversations: "Aucune conversation",
    typeMessage: "Écrivez votre message...",
    send: "Envoyer",
    you: "Vous",
    buyer: "Acheteur",
    seller: "Vendeur",
    broker: "Courtier",
    consultant: "Consultant",
    unread: "non lu(s)",
    messageSent: "Message Envoyé",
    messageSentDesc: "Votre message a été envoyé",
  },
};

export default function PartnerMessages() {
  const { partnerType } = useParams<{ partnerType: string }>();
  const { user } = useAuth();
  const { language } = useLanguage();
  const { toast } = useToast();
  const t = translations[language as keyof typeof translations] || translations.en;

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (user) {
      loadConversations();
    }
  }, [user]);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.id);
    }
  }, [selectedConversation]);

  const loadConversations = async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const conversationsQuery = query(
        collection(db, 'partnerConversations'),
        where('partnerId', '==', user.email),
        orderBy('lastMessageAt', 'desc')
      );
      const snapshot = await getDocs(conversationsQuery);
      const list = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        lastMessageAt: doc.data().lastMessageAt?.toDate(),
      })) as Conversation[];
      setConversations(list.length > 0 ? list : mockConversations);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setConversations(mockConversations);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMessages = async (conversationId: string) => {
    try {
      const messagesQuery = query(
        collection(db, 'partnerMessages'),
        where('conversationId', '==', conversationId),
        orderBy('sentAt', 'asc')
      );
      const snapshot = await getDocs(messagesQuery);
      const list = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        sentAt: doc.data().sentAt?.toDate(),
      })) as Message[];
      setMessages(list.length > 0 ? list : mockMessages.filter(m => m.conversationId === conversationId));
    } catch (error) {
      setMessages(mockMessages.filter(m => m.conversationId === conversationId));
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation || !user) return;
    
    setIsSending(true);
    try {
      const newMsg: Message = {
        id: `msg-${Date.now()}`,
        conversationId: selectedConversation.id,
        senderId: 'partner',
        senderName: t.you,
        content: newMessage,
        sentAt: new Date(),
        read: true,
      };
      setMessages([...messages, newMsg]);
      setNewMessage("");
      toast({ title: t.messageSent, description: t.messageSentDesc });
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const formatMessageTime = (date: Date) => {
    const now = new Date();
    const diffHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffHours < 24) return format(date, 'HH:mm');
    if (diffHours < 48) return language === 'fr' ? 'Hier' : 'Yesterday';
    return format(date, 'dd MMM', { locale: language === 'fr' ? fr : undefined });
  };

  const getTypeBadgeVariant = (type: string): "default" | "secondary" | "outline" => {
    switch (type) {
      case 'buyer': return 'default';
      case 'seller': return 'secondary';
      default: return 'outline';
    }
  };

  const filteredConversations = conversations.filter(conv => 
    conv.participantName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.lastMessage.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalUnread = conversations.reduce((sum, conv) => sum + conv.unreadCount, 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t.title}</h1>
          <p className="text-muted-foreground mt-2">{t.subtitle}</p>
        </div>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          {t.newMessage}
        </Button>
      </div>

      {/* Main Content */}
      <div className="grid md:grid-cols-3 gap-6 h-[600px]">
        {/* Conversations List */}
        <Card className="md:col-span-1">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">{t.conversations}</CardTitle>
              {totalUnread > 0 && (
                <Badge variant="default">{totalUnread} {t.unread}</Badge>
              )}
            </div>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t.searchConversations}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[480px]">
              {filteredConversations.length > 0 ? (
                <div className="divide-y">
                  {filteredConversations.map((conversation) => (
                    <div
                      key={conversation.id}
                      onClick={() => setSelectedConversation(conversation)}
                      className={`p-4 cursor-pointer hover:bg-muted/50 transition-colors ${
                        selectedConversation?.id === conversation.id ? 'bg-muted' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-primary/10 text-primary text-sm">
                            {conversation.participantName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="font-semibold text-sm truncate">{conversation.participantName}</h4>
                            <span className="text-xs text-muted-foreground">
                              {formatMessageTime(conversation.lastMessageAt)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant={getTypeBadgeVariant(conversation.participantType)} className="text-xs">
                              {t[conversation.participantType as keyof typeof t]}
                            </Badge>
                            {conversation.unreadCount > 0 && (
                              <Badge variant="default" className="text-xs">{conversation.unreadCount}</Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground truncate">{conversation.lastMessage}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <MessageSquare className="h-10 w-10 text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground text-center">{t.noConversations}</p>
                </div>
              )}
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Messages Area */}
        <Card className="md:col-span-2">
          {selectedConversation ? (
            <>
              <CardHeader className="border-b pb-4">
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {selectedConversation.participantName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <CardTitle className="text-lg">{selectedConversation.participantName}</CardTitle>
                    <CardDescription className="flex items-center gap-2">
                      <Badge variant={getTypeBadgeVariant(selectedConversation.participantType)} className="text-xs">
                        {t[selectedConversation.participantType as keyof typeof t]}
                      </Badge>
                      {selectedConversation.mandateName && (
                        <span className="text-xs">• {selectedConversation.mandateName}</span>
                      )}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0 flex flex-col h-[480px]">
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${message.senderId === 'partner' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] rounded-lg p-3 ${
                            message.senderId === 'partner'
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-muted'
                          }`}
                        >
                          <p className="text-sm">{message.content}</p>
                          <p className={`text-xs mt-1 ${
                            message.senderId === 'partner' ? 'text-primary-foreground/70' : 'text-muted-foreground'
                          }`}>
                            {format(message.sentAt, 'HH:mm')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
                <div className="p-4 border-t">
                  <div className="flex gap-2">
                    <Input
                      placeholder={t.typeMessage}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      disabled={isSending}
                    />
                    <Button onClick={handleSendMessage} disabled={isSending || !newMessage.trim()}>
                      <Send className="h-4 w-4 mr-1" />
                      {t.send}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </>
          ) : (
            <CardContent className="flex flex-col items-center justify-center h-full">
              <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold">{t.selectConversation}</h3>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
}
